import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Typography,
  CircularProgress,
  TextField,
  Grid,
  Button,
  Paper,
  Box,
} from "@mui/material";
import dayjs from "dayjs";
import {
  getProcurementDetail,
  updateProcurementPickup,
} from "../../api/biomassProcurement";
import { useTranslation } from "react-i18next";
import { useAlert } from "../../components/Alert";
import CustomAppBar from "../../components/AppBar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";

const PickableProcurement = ({ procurement }) => {
  const { t } = useTranslation();
  if (!procurement) {
    return null;
  }
  return (
    <Paper
      sx={{
        padding: 2,
        maxWidth: 600,
        margin: "auto",
        paddingBottom: 4,
        mt: 2,
      }}
    >
      <Typography variant="h6">{t("Pickup Details")}</Typography>
      <Typography>
        <strong>{t("Farmer Name")}:</strong> {procurement.farmer_name}
      </Typography>
      <Typography>
        <strong>{t("Mobile")}:</strong> {procurement.mobile}
      </Typography>
      <Typography>
        <strong>{t("Crop Name")}:</strong> {procurement.crop_name}
      </Typography>
      <Typography>
        <strong>{t("Acreage")}:</strong> {procurement.acreage}
      </Typography>
      <Typography>
        <strong>{t("Address")}:</strong> {procurement.address_line1},{" "}
        {procurement.village}, {procurement.postoffice_name},{" "}
        {procurement.district_name}, {procurement.state_name},{" "}
        {procurement.pincode}
      </Typography>
      <Typography>
        <strong>{t("Estimated Payout (Rs.)")}: </strong>{" "}
        {procurement.estimated_payout}
      </Typography>
    </Paper>
  );
};

const ProcurementPickup = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [procurement, setProcurement] = useState(null);

  const [formData, setFormData] = useState({
    quantity_kg: "",
    paid_amount: "",
    paid_on: dayjs(),
    pickup_remarks: "",
    pickup_proof: null,
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDateChange = (value) => {
    setFormData({ ...formData, inspected_on: value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, pickup_proof: file });
  };

  const fetchProcurementDetails = async () => {
    try {
      const response = await getProcurementDetail(id);
      setProcurement(response);
      setFormData({
        ...formData,
        quantity_kg: response.quantity_kg,
        paid_amount: response.estimated_payout,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching pickup details:", error);
      showAlert("error", "Failed to fetch pickup details");
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const errors = {};
    if (!formData.quantity_kg) {
      errors.quantity_kg = "Quantity is required";
    }
    if (!formData.paid_amount) {
      errors.paid_amount = "Paid amount is required";
    }
    if (!formData.paid_on) {
      errors.paid_on = "Paid on is required";
    }

    setErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    try {
      const request_data = {
        quantity_kg: formData.quantity_kg,
        status: "PICKED",
        paid_amount: formData.paid_amount,
        paid_on: dayjs(formData.visiting_on).format(),
        pickup_remarks: formData.pickup_remarks || "",
      };
      if (formData.pickup_proof) {
        request_data.pickup_proof = formData.pickup_proof;
      }
      const response = await updateProcurementPickup(id, request_data);
      console.log("Procurement updated:", response);
      showAlert("success", t(`Pickup successful`));
      if (procurement.pickup_order) {
        navigate(`/pickup-order/${procurement.pickup_order}`);
      } else {
        navigate("/procurement-list");
      }
    } catch (error) {
      console.error("Error updating inspection:", error);
      showAlert("error", "Failed to update pickup");
    }
  };

  useEffect(() => {
    fetchProcurementDetails();
  }, []);

  return (
    <>
      <CustomAppBar />
      {loading ? (
        <CircularProgress />
      ) : procurement ? (
        <Box sx={{ padding: 2 }}>
          <PickableProcurement procurement={procurement} />
          <Typography variant="h6" marginBottom={1} marginTop={2}>
            {t("Pickup Details")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="quantity_kg"
                value={formData.quantity_kg}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                label={t("Quantity (kg)")}
                error={!!errors.quantity_kg}
                helperText={errors.quantity_kg}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="paid_amount"
                value={formData.paid_amount}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                label={t("Paid Amount")}
                error={!!errors.paid_amount}
                helperText={errors.paid_amount}
              />
            </Grid>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DateTimePicker"]}>
                  <DateTimePicker
                    label={t("Paid On")}
                    name="paid_on"
                    value={formData.paid_on}
                    onChange={handleDateChange}
                    error={!!errors.paid_on}
                    helperText={errors.paid_on}
                    required
                    fullWidth
                  />
                </DemoContainer>
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="pickup_remarks"
                value={formData.pickup_remarks}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                label={t("Pickup Remarks")}
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={12}>
              <input
                accept="image/*"
                id="contained-button-file"
                multiple
                type="file"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <label htmlFor="contained-button-file">
                <Button
                  variant="contained"
                  color="primary"
                  component="span"
                  fullWidth
                >
                  {t("Upload Proof")}
                </Button>
              </label>
              {formData.pickup_proof && (
                <Typography variant="body1">
                  {formData.pickup_proof.name}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => await handleSubmit()}
                fullWidth
              >
                {t("Accept")}
              </Button>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Typography variant="h6">{t("Pickup not found")}</Typography>
      )}
    </>
  );
};

export default ProcurementPickup;
